import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { userService } from "../services/userService";
import {
  UserValuesInit,
  getUserCreateVariables,
  getUserValues,
  UserValuesType,
  UserErrorsType,
  userDisplayId,
} from "./UserCommonStore";
import { User } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getRoleValues,
  RoleValuesType,
} from "../../role/store/RoleCommonStore";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import {
  getTenantValues,
  TenantValuesType,
} from "../../tenant/store/TenantCommonStore";
import { isNil } from "../../../common/utils";
import { validateMask } from "../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { Language } from "../../../api/graphql";
import { IdCardType } from "../../../api/graphql";
import { Gender } from "../../../api/graphql";
import { roleService } from "../../role/services/roleService";
import { tenantService } from "../../tenant/services/tenantService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class UserCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: UserErrorsType = {};
  service = userService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    main: { open: true, errors: [] },
    details: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: User | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = UserValuesInit();
  nameError: string | undefined;
  emailError: string | undefined;
  roleError: string | undefined;
  roleService = roleService;
  roleSearchValue: string = "";
  roleOptions: RoleValuesType[] = [];
  languageError: string | undefined;
  avatarError: string | undefined;
  targetTenantError: string | undefined;
  targetTenantService = tenantService;
  targetTenantSearchValue: string = "";
  targetTenantOptions: TenantValuesType[] = [];
  activeError: string | undefined;
  cellphoneError: string | undefined;
  twoFaseAuthError: string | undefined;
  idCardTypeError: string | undefined;
  idCardError: string | undefined;
  genderError: string | undefined;

  private customValidators: CustomValidator<UserValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<UserValuesType>);
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<UserValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return userDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "role") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showRole = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.roleOne({ id: params.returnData.resultId });
          const role = result.roleOne;
          this.entityValues.showRole = {
            displayId: role.displayId,
            id: role.id,
          };
          this.setRoleOptions([getRoleValues(role, this.langStore)]);
        }
      } else if (params.callerData?.fieldName === "targetTenant") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showTargetTenant = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.tenantOne({
            id: params.returnData.resultId,
          });
          const tenant = result.tenantOne;
          this.entityValues.showTargetTenant = {
            displayId: tenant.displayId,
            id: tenant.id,
          };
          this.setTargetTenantOptions([
            getTenantValues(tenant, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "main",
        label: this.rootStore.langStore.groupUserMainLabel,
        errors: this.getCardErrors("main"),
      },
      {
        id: "details",
        label: this.rootStore.langStore.groupUserDetailsLabel,
        errors: this.getCardErrors("details"),
      },
    ];
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getEmailValue(): string {
    return this.entityValues.email;
  }

  setEmailValue(value: string) {
    this.entityValues.email = value;
  }

  setRoleOptions(values: RoleValuesType[]) {
    this.roleOptions = values;
  }

  *loadRoleValues(): any {
    let where: any = null;
    if (this.roleSearchValue) {
      const searchFilter = {
        [this.langStore.fieldByLang("name")]: {
          contains: this.roleSearchValue,
          mode: "insensitive",
        },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.roleListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setRoleOptions(
      (result.roleListWithOffset || []).map((x: any) =>
        getRoleValues(x, this.langStore)
      )
    );
  }

  getRoleOptions() {
    return this.roleOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setRoleValue(value: string) {
    this.roleSearchValue = value;
    yield this.loadRoleValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedRole(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewRole(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "role"
      );
    } else {
      if (value) {
        this.entityValues.showRole = value;
        this.entityValues.__role = value.displayId;
      }
    }
  }

  getLanguageValue(): Language {
    return this.entityValues.language;
  }

  setLanguageValue(value: Language) {
    this.entityValues.language = value;
  }

  getLanguageOptions() {
    return this.langStore.LanguageOptions;
  }

  *avatarUploadBlob(blob: Blob): any {
    const previousFiles = [...this.entityValues.__avatar_files];
    const previousName = this.entityValues.avatar;
    const file = new File([blob], "avatar.jpg", {
      type: blob.type || "image/jpeg",
    });
    const fileId = `avatar_${Date.now()}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__avatar_files = [newFile];
    this.entityValues.avatar = file.name;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__avatar_files = previousFiles;
      this.entityValues.avatar = previousName;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("avatar");
      } else {
        this.setFieldError(
          "avatar",
          this.langStore.UserAvatarLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("avatar");
    const fileIndex = this.entityValues.__avatar_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__avatar_files[fileIndex].ticket = outcome.ticket;
    }
  }

  avatarDelete() {
    this.entityValues.__avatar_files = [];
    this.entityValues.avatar = "";
  }

  setTargetTenantOptions(values: TenantValuesType[]) {
    this.targetTenantOptions = values;
  }

  *loadTargetTenantValues(): any {
    let where: any = null;
    if (this.targetTenantSearchValue) {
      const searchFilter = {
        name: { contains: this.targetTenantSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setTargetTenantOptions(
      (result.tenantListWithOffset || []).map((x: any) =>
        getTenantValues(x, this.langStore)
      )
    );
  }

  getTargetTenantOptions() {
    return this.targetTenantOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTargetTenantValue(value: string) {
    this.targetTenantSearchValue = value;
    yield this.loadTargetTenantValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedTargetTenant(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewTenant(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "targetTenant"
      );
    } else {
      if (value) {
        this.entityValues.showTargetTenant = value;
        this.entityValues.__targetTenant = value.displayId;
      }
    }
  }

  getActiveValue(): boolean {
    return this.entityValues.active;
  }

  setActiveValue(value: boolean) {
    this.entityValues.active = value;
  }

  getCellphoneValue(): string {
    return this.entityValues.cellphone;
  }

  setCellphoneValue(value: string) {
    this.entityValues.cellphone = value;
  }

  getTwoFaseAuthValue(): boolean {
    return this.entityValues.twoFaseAuth;
  }

  setTwoFaseAuthValue(value: boolean) {
    this.entityValues.twoFaseAuth = value;
  }

  getIdCardTypeValue(): IdCardType | "" {
    return this.entityValues.idCardType;
  }

  setIdCardTypeValue(value: IdCardType | "") {
    this.entityValues.idCardType = value;
  }

  getIdCardTypeOptions() {
    return this.langStore.IdCardTypeOptions;
  }

  getIdCardValue(): string {
    return this.entityValues.idCard;
  }

  setIdCardValue(value: string) {
    this.entityValues.idCard = value;
  }

  getGenderValue(): Gender | "" {
    return this.entityValues.gender;
  }

  setGenderValue(value: Gender | "") {
    this.entityValues.gender = value;
  }

  getGenderOptions() {
    return this.langStore.GenderOptions;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as UserErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as UserErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  addNewRole(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "User",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/role/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTenant(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "User",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/tenant/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = UserValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      main: { open: true, errors: [] },
      details: { open: true, errors: [] },
    };
    this.roleSearchValue = "";
    this.roleOptions = [];
    this.targetTenantSearchValue = "";
    this.targetTenantOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: UserValuesType): UserErrorsType {
    const errors: UserErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserNameLabel,
      };
      this.setCardError("main", errors.name.message);
    }
    values.email = values.email.trim();
    if (values.email === "") {
      errors.email = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserEmailLabel,
      };
      this.setCardError("main", errors.email.message);
    }

    if (!values.language) {
      errors.language = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserLanguageLabel,
      };
      this.setCardError("main", errors.language.message);
    }
    {
      const pendingUpload = values.__avatar_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.avatar = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.UserAvatarLabel,
        };
        this.setCardError("main", errors.avatar.message);
      }
    }

    if (isNil(values.active)) {
      errors.active = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserActiveLabel,
      };
      this.setCardError("main", errors.active.message);
    }
    if (
      values.idCard &&
      values.idCardType &&
      ["dni"].includes(values.idCardType)
    ) {
      if (
        !["999999999", "99999999", "9999999"].some((m) =>
          validateMask(values.idCard, { mask: m })
        )
      ) {
        errors.idCard = {
          message: this.langStore.maskedField("999999999 | 99999999 | 9999999"),
          fieldLabel: this.langStore.UserIdCardLabel,
        };
      }
    } else if (
      values.idCard &&
      values.idCardType &&
      ["cuitCuil"].includes(values.idCardType)
    ) {
      if (
        !["99-99999999-9", "99-9999999-9"].some((m) =>
          validateMask(values.idCard, { mask: m })
        )
      ) {
        errors.idCard = {
          message: this.langStore.maskedField("99-99999999-9 | 99-9999999-9"),
          fieldLabel: this.langStore.UserIdCardLabel,
        };
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "UserCreateStore"
    );
  }

  *duplicatesUser(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const userInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (userInstance) {
      this.entityValues = getUserValues(userInstance, this.langStore, true);
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(`/user/update/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewUser(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewUser(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      email: this.entityValues.email,
      language: this.entityValues.language,
      active: this.entityValues.active,
      cellphone: this.entityValues.cellphone,
      twoFaseAuth: this.entityValues.twoFaseAuth,
      idCardType: this.entityValues.idCardType,
      idCard: this.entityValues.idCard,
      gender: this.entityValues.gender,
      role: this.entityValues.showRole?.id || null,
      targetTenant: this.entityValues.showTargetTenant?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getUserValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = UserValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getUserCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.userCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.userCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesUser(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.email = initialDisplayId;
      }

      yield this.loadRoleValues();
      yield this.loadTargetTenantValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
